import type { NostrEvent } from '@nostrify/nostrify';
import { getPostImage, getPostVideo, type PostVideo } from '@/lib/postImage';

export interface PostMeta {
  /** Featured image URL, if any. */
  image?: string;
  /** Featured looping video, if the post contains one. */
  video?: PostVideo;
  /** Derived headline (first sentence / line, truncated). */
  title: string;
  /** Longer excerpt for list previews. */
  excerpt: string;
  /** First hashtag `t` tag, lowercased, if any. */
  tag?: string;
  /** Human-readable post date, e.g. "May 20, 2026". */
  date: string;
}

const MEDIA_URL = /https?:\/\/\S+/g;
const NOSTR_URI = /nostr:[a-z0-9]+/gi;

/** Strip URLs and nostr: references, collapse whitespace. */
function cleanText(content: string): string {
  return content
    .replace(MEDIA_URL, '')
    .replace(NOSTR_URI, '')
    .replace(/#(\w+)/g, '$1') // keep hashtag words as plain text
    .replace(/\s+/g, ' ')
    .trim();
}

/** Truncate at a word boundary and add an ellipsis. */
function truncate(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastSpace = slice.lastIndexOf(' ');
  return `${slice.slice(0, lastSpace > 0 ? lastSpace : max).trim()}…`;
}

export function formatPostDate(timestamp: number): string {
  return new Date(timestamp * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function getPostMeta(event: NostrEvent): PostMeta {
  const image = getPostImage(event);
  const video = getPostVideo(event);

  const clean = cleanText(event.content);

  // Title: first sentence or first line, whichever is shorter.
  const firstLine = clean.split('\n')[0]?.trim() || clean;
  const firstSentence = clean.split(/(?<=[.!?])\s/)[0]?.trim() || clean;
  const rawTitle = (firstSentence.length < firstLine.length
    ? firstSentence
    : firstLine) || 'Untitled post';

  const title = truncate(rawTitle, 80);
  const excerpt = truncate(clean, 180);

  // First hashtag tag.
  const tag = event.tags.find(([name]) => name === 't')?.[1]?.toLowerCase();

  return {
    image,
    video,
    title,
    excerpt,
    tag,
    date: formatPostDate(event.created_at),
  };
}
